import {useLazyQuery} from "@apollo/client/react";
import {
    CtrDomainsVf,
    CtrDynamicTables,
    CtrMetaField,
    CtrMetaFieldInput,
    Maybe,
    Mutation,
    MutationUpdateCtrMetaFieldArgs,
    Query
} from "../graphql/generated/graphql-main.tsx";
import {apollo11, gqlTemplates} from "../utils/gql-client.ts";
import {useWpContext} from "../components/context/WordPressProvider.tsx";
import {findBy} from "../utils/array-utils.ts";
import {coreActions} from "../store/slice-core";
import {useXDispatch, useXSelector} from "../store/hooks.ts";
import React, {useEffect} from "react";
import useCtrValeursFixes from "./useCtrValeursFixes.ts";
import useReactTexts from "./useReactTexts.ts";
import clsx from "clsx";

// custom options to pass to 'toHtml'
type ToHtmlOptions = {
    // display value in currency format
    type?: "currency" | "percent" | "numeric";
    // show label as bold or not (default = true)
    bold?: boolean;
    // display label & value on 1 line instead of 2 lines (span vs div)
    uniline?: boolean;
    // whether to return as <dt><dd> or simple <div>s -- depends on where toHtml is called (default: false)
    noDef?: boolean;
    // pass VALEURS_FIXES domain to use for interpreting value
    domain?: CtrDomainsVf;
    // anything to append after value (e.g. unit of measurement)
    suffix?: string;
    // custom function to further parse value from other tables (e.g. GENRES_PROPRIETES, etc.)
    getter?: (val: Maybe<string>) => string;
}

const gk: keyof Query = "ctrMetaFields";
const set = coreActions.$setMetaFields;

/**
 * @param [detailed=false] whether to use extended query to fetch more fields or only strict minimum fields
 */
export default function useCtrMetaFields(detailed: boolean = false) {
    const dispatch = useXDispatch();
    const {is_fr} = useWpContext();
    const {fixedValue} = useCtrValeursFixes();
    const {__} = useReactTexts();

    const $entries = useXSelector(s => s.core.meta_fields);

    const queryNode = detailed ? gqlTemplates.metaFields2 : gqlTemplates.metaFields;
    const [fetch, {loading}] = useLazyQuery<Query>(queryNode, {fetchPolicy: "cache-and-network"});

    const load = async () => {
        const {data} = await fetch();
        if (data?.[gk] == null) return;
        dispatch(set(data?.[gk] as CtrMetaField[]));
    }

    // get all meta-fields for the specified table
    const fieldsByTable = (table: CtrDynamicTables) => {
        return $entries.filter(it => it?.table == table);
    }

    // get proper FR/EN label for field name given its table
    const toLabel = <T, >(table: CtrDynamicTables, fld: keyof T) => {
        const _fields = fieldsByTable(table);
        const _field = findBy(_fields, "name", fld);
        return is_fr ? _field?.label_fr : _field?.label_en;
    }

    /**
     * Return properly formatted value w/o the label (e.g. display price w/o label 'Prix : ')
     * Given entry, table, and field name, return ONLY the value for that field in the given entry
     * properly formatted based on options.
     * @param entry
     */
    const toValue = <T, >(entry: Maybe<T>) => {

        return function <S = {}>(fldName: keyof T, options?: ToHtmlOptions) {
            // get value from listing (type CtrInscriptions)
            let value: Maybe<string> = entry?.[fldName] as string | null;
            // if domain is specified, get fixed value for the value (e.g. IS = Détaché (Isolé) in TYPE_BATIMENT domain)
            if (options?.domain != null) {
                value = fixedValue(options.domain, value);
            }

            // custom value getter passed to do further data conversion on a given value
            if (options?.getter != null) {
                value = options?.getter(value);
            }

            // return null will cause prevent rendering of any html
            if (value == null) return null;

            // custom formatter for currency-type numeric formats
            if (options?.type == "currency") {
                const Dollar = new Intl.NumberFormat(is_fr ? 'fr-CA' : 'en-CA', {
                    style: "currency",
                    currency: 'CAD',
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0
                });
                value = Dollar.format(parseFloat(value) || 0);
            }
            // normal numerical value (format w/ proper thousands separator, etc.)
            if (options?.type == "numeric") {
                const Numer = new Intl.NumberFormat(is_fr ? 'fr-CA' : 'en-CA');
                value = Numer.format(parseFloat(value) || 0);
            }
            if (options?.type == "percent") {
                value = `${value} %`;
            }
            return value;
        }
    }

    /**
     * Return HTML node w/ label + properly formatted value.
     * To simplify things, pass initial entry & type of dynamic table from which to fetch things.
     * @param entry
     * @param tbl
     */
    const toHtml = <T, >(entry: Maybe<T>, tbl: CtrDynamicTables) => {

        // optionally provide generic type for the 'options.other' parameters
        return function <S = {}>(fldName: keyof T, options?: ToHtmlOptions) {
            // get label in proper language
            const lbl = toLabel(tbl, fldName as string);
            let val = toValue<T>(entry)(fldName, options);
            // null causes html node to NOT be rendered at all
            if (val == null) return null;

            // can disable bold for label
            const htmlLabel = options?.bold == false ? lbl : <span className={"font-bold meta-label"}>{lbl}</span>;

            // handle 'O' = Oui, 'N' = Non cases -- assume that O/N always equal Oui/Non -- not fool-proof, but ok
            if (val == 'O') val = __('Oui');
            if (val == 'N') val = __('Non');

            // return user-friendly html for label & value
            if (options?.noDef == true) {
                // if explicitly set to 'asDefinition:false', return <div> pair instead
                return <div className={clsx({"*:inline": options?.uniline})}>
                    <div>{htmlLabel}</div>
                    {options?.uniline ? " : " : ""}
                    <div>{val || "nd"} {options?.suffix}</div>
                </div>
            } else {
                // by default, assume output must be returned as <dt><dd> pair
                return <dl className={clsx({"*:inline": options?.uniline})}>
                    <dt>{htmlLabel}</dt>
                    {options?.uniline ? " : " : ""}
                    <dd>{val || "nd"} {options?.suffix}</dd>
                </dl>
            }
        }
    }

    const updateMetaField = async (entry: CtrMetaFieldInput) => {
        const {data} = await apollo11.mutate<Mutation, MutationUpdateCtrMetaFieldArgs>({
            mutation: gqlTemplates.updateMetaField,
            variables: {entry: entry}
        });

        dispatch(coreActions.$updateMetaField(entry));
        return data?.updateCtrMetaField == true;
    }

    useEffect(() => {
        if ($entries.length > 0) return;
        load();
    }, [$entries]);

    return {
        $entries: $entries,
        loading: loading,
        toLabel: toLabel,
        toValue: toValue,
        toHtml: toHtml,
        updateIt: updateMetaField
    }
}

